/**
 * Content catalogue for the SovEU sector pages under /sectoren/.
 * The overview page /voor-wie remains the hub and links to all entries here.
 * Shape is identical to SERVICES in data/services.js.
 */
export const SECTORS = [
  {
    slug: "gemeenten",
    to: "/sectoren/gemeenten",
    h1: "AI governance en datagovernance voor gemeenten",
    navLabel: "Gemeenten",
    anchorText: "AI governance en datagovernance voor gemeenten",
    seoTitle: "AI governance en datagovernance voor gemeenten | SovEU",
    seoDescription:
      "SovEU helpt gemeenten met AI governance, algoritmeregister, AVG, datalekrisico, digitale afhankelijkheid en bestuurlijke regie.",
    summary:
      "Bestuurlijke regie op algoritmes, AI gebruik, datarisico's en digitale afhankelijkheid binnen het gemeentelijk domein.",
    intro: [
      "Gemeenten verwerken de meest gevoelige gegevens van inwoners: in het sociaal domein, bij schuldhulpverlening, bij toezicht en handhaving, bij belastingen en bij vergunningverlening. Tegelijk groeit het gebruik van algoritmes, AI ondersteuning en cloudplatforms in vrijwel elk domein. Daarmee groeit ook de vraag naar verantwoording.",
      "SovEU werkt als onafhankelijke regiepartij voor gemeenten. Wij brengen AI gebruik, algoritmes, datastromen en digitale afhankelijkheden samen in één bestuurlijk risicobeeld, zodat college, directie en raad kunnen besluiten op basis van feiten in plaats van aannames.",
    ],
    sections: [
      {
        h2: "Waar gemeenten tegenaan lopen",
        bullets: [
          "Geen actueel, centraal overzicht van algoritmes en AI toepassingen per domein",
          "Een algoritmeregister dat is gestart maar niet wordt bijgehouden",
          "Generatieve AI die informeel wordt gebruikt bij inwonerscontact, bezwaar en Woo verzoeken",
          "Gevoelige dossiers in mail, netwerkschijven en samenwerkingsomgevingen zonder duidelijk beheer",
          "Ruime toegangsrechten die in de loop van jaren zijn gegroeid",
          "Kritieke processen die volledig afhangen van één niet Europese leverancier",
          "Vragen van raad, rekenkamer, media of toezichthouders die niet snel te beantwoorden zijn",
        ],
      },
      {
        h2: "Bestuurlijke context in het gemeentelijk domein",
        paragraphs: [
          "In een gemeente zit de verantwoordelijkheid voor digitale keuzes zelden op één plek. De techniek is belegd bij informatiemanagement of een samenwerkingsverband, de juridische kant bij de concernjurist en de FG, de veiligheid bij de CISO en de inhoudelijke keuze bij de domeindirecteur. Het college is uiteindelijk politiek verantwoordelijk en de raad controleert.",
          "Dat maakt de behoefte aan één gezamenlijk beeld groot. Niet een technisch rapport per systeem, maar een samenhangend risicobeeld waarin duidelijk is welke onderwerpen bestuurlijke aandacht vragen, wie eigenaar is en welke stap logisch volgt.",
        ],
      },
      {
        h2: "Wat SovEU voor gemeenten in kaart brengt",
        bullets: [
          "Algoritmes en AI toepassingen per domein, inclusief rol in besluitvorming",
          "Menselijke tussenkomst bij algoritmische besluitvorming en hoe die feitelijk werkt",
          "Status en kwaliteit van algoritmeregistratie en het interne AI register",
          "AVG praktijk: verwerkingsregister, grondslagen, DPIA's, bewaartermijnen",
          "Datalekrisico: dataconcentraties, ongestructureerde opslag, toegangsrechten",
          "Digitale afhankelijkheid: cloudplatforms, jurisdictie, CLOUD Act, vendor lock in",
          "Eigenaarschap en governance: wie besluit, wie beheert, wie ziet toe",
        ],
      },
      {
        h2: "Wat het gemeenten oplevert",
        bullets: [
          "Een bestuurlijk risicobeeld dat bruikbaar is in college en directie",
          "Onderbouwde antwoorden op vragen van raad, rekenkamer en toezichthouders",
          "Een werkbare route naar actuele algoritmeregistratie en AI documentatie",
          "Prioritering: welke risico's eerst, welke kunnen wachten",
          "Aantoonbare beheersing van AI gebruik, datarisico's en leveranciersketens",
        ],
      },
      {
        h2: "Onafhankelijke positie",
        paragraphs: [
          "SovEU levert geen software, geen platform en geen implementatiecapaciteit. Wij hebben daarmee geen belang bij een bepaalde uitkomst. Die onafhankelijke positie is bewust gekozen: een bestuurlijk risicobeeld is alleen bruikbaar als het niet tegelijk een aanbieding is.",
        ],
      },
    ],
    faqs: [
      {
        q: "Werkt SovEU ook voor kleinere gemeenten?",
        a: "Ja. Bij kleinere gemeenten is de capaciteit vaak beperkt en de afhankelijkheid van leveranciers en samenwerkingsverbanden juist groter. Het risicobeeld wordt afgestemd op de omvang en context van de organisatie.",
      },
      {
        q: "Vervangt dit onze FG, CISO of privacy officer?",
        a: "Nee. Wij werken samen met de bestaande rollen en versterken die. SovEU levert de onafhankelijke bestuurlijke laag, niet de uitvoering van interne functies.",
      },
      {
        q: "Kan SovEU helpen bij vragen uit de raad?",
        a: "Ja. Wij ondersteunen bij het opbouwen van een feitelijk en uitlegbaar beeld dat gebruikt kan worden in raadsinformatie, memo's en verantwoording.",
      },
      {
        q: "Hoe verhoudt dit zich tot de AI Act?",
        a: "Gemeenten zijn in de meeste gevallen gebruiker van AI systemen. Wij duiden welke verplichtingen relevant zijn, hoe die samenhangen met AVG en algoritmeregistratie, en welke voorbereiding realistisch is.",
      },
    ],
    praktijkvoorbeeld: {
      title:
        "Een middelgrote gemeente die grip zocht op algoritmes en AI gebruik",
      situatie: [
        "Een gemeente met ongeveer 80.000 inwoners kreeg raadsvragen over het gebruik van algoritmes in het sociaal domein. Er was ooit gestart met een algoritmeregister, maar het werd niet bijgehouden en een centraal overzicht van AI toepassingen ontbrak. Tegelijk gebruikten medewerkers generatieve AI bij inwonerscontact zonder gedeeld kader.",
        "SovEU bracht in enkele weken het feitelijke gebruik van algoritmes, AI toepassingen en datastromen per domein samen in één bestuurlijk risicobeeld, met eigenaarschap en een heldere prioritering.",
      ],
      resultaat: [
        "College en directie konden raadsvragen onderbouwd beantwoorden",
        "Een werkbare route naar actuele algoritmeregistratie lag klaar",
        "Prioritering maakte duidelijk welke onderwerpen eerst aandacht vroegen",
      ],
    },
    relatedServices: [
      "algoritmeregister-gemeenten",
      "bestuursscan-ai-en-data",
      "data-datalek-risicoscan",
      "regiebeeld-digitale-afhankelijkheden",
    ],
    relatedArticles: [
      "algoritmeregister-verplicht-gemeenten",
      "datalek-gemeente-onderwijs-avg-data-risicos",
      "digitale-afhankelijkheid-overheid-cloud-act",
    ],
    ctaTitle: "Bestuurlijke regie voor uw gemeente",
    ctaText:
      "Wij bespreken graag welke vragen binnen uw gemeente spelen rond algoritmes, AI gebruik, datarisico's en digitale afhankelijkheid.",
    ctaLabel: "Plan een vrijblijvend gesprek",
  },

  {
    slug: "onderwijsinstellingen",
    to: "/sectoren/onderwijsinstellingen",
    h1: "AI governance en dataregie voor onderwijsinstellingen",
    navLabel: "Onderwijsinstellingen",
    anchorText: "AI governance en dataregie voor onderwijsinstellingen",
    seoTitle:
      "AI governance, AVG en datalekrisico voor onderwijsinstellingen | SovEU",
    seoDescription:
      "SovEU helpt hogescholen, universiteiten en onderwijsinstellingen met AI gebruik, AVG, datalekken, Canvas risico's en dataregie.",
    summary:
      "Dataregie en AI governance voor hogescholen, universiteiten en onderwijsinstellingen, van leeromgeving tot studentdossier.",
    intro: [
      "Onderwijsinstellingen werken met grote hoeveelheden persoonsgegevens van studenten, deelnemers, docenten en onderzoekers. Die gegevens staan verspreid over studentinformatiesystemen, digitale leeromgevingen zoals Canvas, Moodle of Brightspace, samenwerkingsomgevingen, onderzoeksomgevingen en talloze aanvullende applicaties die per opleiding zijn ingericht.",
      "Daar bovenop komt AI. Studenten en medewerkers gebruiken generatieve AI dagelijks, en AI functionaliteit wordt in steeds meer onderwijssoftware standaard ingeschakeld. SovEU helpt onderwijsinstellingen om AI gebruik, datastromen en datalekrisico bestuurlijk in beeld te brengen en beheersbaar te maken.",
    ],
    sections: [
      {
        h2: "Waar onderwijsinstellingen tegenaan lopen",
        bullets: [
          "Applicatielandschap dat per faculteit, opleiding of dienst is gegroeid",
          "Digitale leeromgevingen met ruime autorisaties en veel gekoppelde tools",
          "Studentdossiers, zorgdossiers en verzuiminformatie in ongestructureerde opslag",
          "Generatieve AI gebruik door studenten en docenten zonder gedeeld kader",
          "AI functies die door leveranciers worden ingeschakeld zonder aparte afweging",
          "Onderzoeksdata met eigen afspraken buiten centrale governance",
          "Datalekken die vaak ontstaan bij exports, mailverkeer en oude bestanden",
        ],
      },
      {
        h2: "AI in het onderwijs vraagt bestuurlijke keuzes",
        paragraphs: [
          "Het gesprek over AI in het onderwijs gaat vaak over toetsing en fraude. Bestuurlijk zijn er echter meer vragen: welke gegevens gaan er in AI tools, wie mag AI functionaliteit aanzetten in een leeromgeving, hoe wordt omgegaan met AI ondersteunde beoordeling van studenten en hoe verantwoordt de instelling dat richting studenten, medezeggenschap en toezicht.",
          "Wij helpen instellingen om die vragen te scheiden van de dagelijkse discussie en te beleggen waar zij horen: bij college van bestuur en directie, met een feitelijk beeld eronder.",
        ],
      },
      {
        h2: "Wat SovEU voor onderwijsinstellingen in kaart brengt",
        bullets: [
          "AI toepassingen in onderwijs, ondersteuning, bedrijfsvoering en onderzoek",
          "AI functionaliteit binnen leeromgevingen en gekoppelde applicaties",
          "Datastromen rond studentgegevens, dossiers en voortgangsinformatie",
          "Toegangsrechten en autorisatiebeheer in leer en samenwerkingsomgevingen",
          "Ongestructureerde opslag: exports, mailbijlagen, oude dossiers, back-ups",
          "AVG praktijk: grondslagen, DPIA's, bewaartermijnen, verwerkersovereenkomsten",
          "Datalekrisico en incidentgereedheid, inclusief de 72 uur praktijk",
          "Leveranciersketens, subverwerkers en jurisdictie van kernleveranciers",
        ],
      },
      {
        h2: "Wat het onderwijsinstellingen oplevert",
        bullets: [
          "Eén bestuurlijk beeld over faculteiten en diensten heen",
          "Prioritering van datalekrisico en AVG aandachtspunten",
          "Een werkbaar kader voor AI gebruik door medewerkers en in leeromgevingen",
          "Onderbouwing richting college van bestuur, medezeggenschap en toezicht",
          "Aantoonbare beheersing van studentgegevens en AI toepassingen",
        ],
      },
      {
        h2: "Onafhankelijke positie",
        paragraphs: [
          "SovEU levert geen onderwijssoftware en geen implementatiediensten. Wij beoordelen onafhankelijk en spreken in bestuurlijke taal, zodat het beeld ook buiten de IT organisatie bruikbaar is.",
        ],
      },
    ],
    faqs: [
      {
        q: "Werkt SovEU voor hogescholen en universiteiten?",
        a: "Ja, en ook voor mbo instellingen, onderwijsstichtingen en samenwerkingsverbanden. De vraagstukken rond AI gebruik, dataregie en datalekrisico komen in alle onderwijssectoren terug.",
      },
      {
        q: "Kijkt SovEU ook naar onderzoeksdata?",
        a: "Waar onderzoeksdata persoonsgegevens bevat en onder institutionele verantwoordelijkheid valt, nemen wij dat mee. De inhoudelijke wetenschappelijke beoordeling laten wij bij de instelling.",
      },
      {
        q: "Gaat dit over AI en toetsfraude?",
        a: "Dat is een onderwijsinhoudelijk vraagstuk. Wij richten ons op governance: welke AI toepassingen zijn er, welke gegevens gaan erin, welke risico's horen daarbij en hoe verantwoordt de instelling dat.",
      },
      {
        q: "Hoe gaat SovEU om met decentrale organisatie?",
        a: "Wij inventariseren juist ook wat buiten de centrale IT om is ingericht. Dat is doorgaans waar de grootste datarisico's zitten.",
      },
    ],
    praktijkvoorbeeld: {
      title: "Een hogeschool met verspreide data en informeel AI gebruik",
      situatie: [
        "Bij een hogeschool met meerdere faculteiten was het applicatielandschap per opleiding gegroeid. Studentdossiers stonden deels in ongestructureerde opslag en in de digitale leeromgeving waren de autorisaties in de loop van jaren ruim geworden. Docenten en studenten gebruikten generatieve AI dagelijks, terwijl een leverancier AI functies had ingeschakeld zonder aparte afweging.",
        "SovEU bracht AI toepassingen, datastromen en datalekrisico over faculteiten heen in beeld en vertaalde dit naar een bestuurlijk beeld voor het college van bestuur, met een werkbaar kader voor AI gebruik.",
      ],
      resultaat: [
        "Eén overzicht over faculteiten en diensten heen",
        "Datalekrisico's rond exports en oude dossiers geprioriteerd",
        "Een kader voor AI gebruik dat aansloot op de dagelijkse praktijk",
      ],
    },
    relatedServices: [
      "data-datalek-risicoscan",
      "ai-beleid-opstellen",
      "bestuursscan-ai-en-data",
      "ai-act-quickscan",
    ],
    relatedArticles: [
      "datalek-gemeente-onderwijs-avg-data-risicos",
      "ai-act-readiness-2026-mkb-adviesbureaus",
      "digitale-afhankelijkheid-overheid-cloud-act",
    ],
    ctaTitle: "Dataregie voor uw instelling",
    ctaText:
      "Wij verkennen graag welke datarisico's en AI vraagstukken binnen uw onderwijsinstelling bestuurlijke aandacht vragen.",
    ctaLabel: "Neem contact op",
  },

  {
    slug: "woningcorporaties",
    to: "/sectoren/woningcorporaties",
    h1: "AI governance en datagovernance voor woningcorporaties",
    navLabel: "Woningcorporaties",
    anchorText: "AI governance en datagovernance voor woningcorporaties",
    seoTitle: "AI governance en datagovernance voor woningcorporaties | SovEU",
    seoDescription:
      "SovEU helpt woningcorporaties met AI gebruik, AVG, datarisico's, leveranciersafhankelijkheid en digitale regie.",
    summary:
      "Grip op AI gebruik, huurdersdata, leveranciersafhankelijkheid en digitale regie binnen de corporatiesector.",
    intro: [
      "Woningcorporaties werken met gegevens die direct raken aan het bestaan van mensen: inkomensgegevens, betalingsachterstanden, meldingen van overlast, zorgsignalen en woonfraudedossiers. Tegelijk is de sector sterk afhankelijk van externe leveranciers, ketenpartners en gestandaardiseerde platforms voor primaire processen.",
      "SovEU helpt woningcorporaties om AI gebruik, datastromen, datarisico's en leveranciersafhankelijkheid bestuurlijk in beeld te brengen. Zodat directeur bestuurder en raad van commissarissen kunnen verantwoorden hoe de corporatie omgaat met huurdersdata en digitale keuzes.",
    ],
    sections: [
      {
        h2: "Waar woningcorporaties tegenaan lopen",
        bullets: [
          "Huurdersdossiers en betalingsgegevens verspreid over meerdere systemen",
          "Ketensamenwerking met gemeenten, zorg en handhaving zonder scherpe dataafspraken",
          "AI functionaliteit in klantcontact, planning, onderhoud en woningtoewijzing",
          "Sterke afhankelijkheid van een beperkt aantal sectorleveranciers",
          "Weinig zicht op subverwerkers achter hoofdleveranciers",
          "Ongestructureerde opslag met gevoelige dossiers en exports",
          "Toenemende verantwoordingsvragen van raad van commissarissen en toezicht",
        ],
      },
      {
        h2: "AI en algoritmische keuzes in de corporatiesector",
        paragraphs: [
          "Toewijzing, urgentiebepaling, risicoselectie bij betalingsachterstanden en signalering rond woonfraude zijn processen met grote gevolgen voor huurders. Wanneer daarin modellen, scores of AI ondersteuning worden gebruikt, is uitlegbaarheid geen luxe maar een voorwaarde.",
          "Wij brengen in kaart waar dit type ondersteuning wordt gebruikt, hoe menselijke tussenkomst is geregeld en of de corporatie kan onderbouwen waarom een uitkomst tot stand is gekomen.",
        ],
      },
      {
        h2: "Wat SovEU voor woningcorporaties in kaart brengt",
        bullets: [
          "AI toepassingen en modellen in klantcontact, toewijzing, onderhoud en debiteurenbeheer",
          "Datastromen rond huurdersgegevens en ketenuitwisseling",
          "AVG praktijk: grondslagen, DPIA's, bewaartermijnen, verwerkersovereenkomsten",
          "Datalekrisico: dataconcentraties, toegangsrechten, ongestructureerde opslag",
          "Leveranciersafhankelijkheid, concentratierisico en vendor lock in",
          "Jurisdictie van leveranciers en subverwerkers",
          "Eigenaarschap en governance richting directie en raad van commissarissen",
        ],
      },
      {
        h2: "Wat het woningcorporaties oplevert",
        bullets: [
          "Een bestuurlijk risicobeeld voor directeur bestuurder en raad van commissarissen",
          "Prioritering van maatregelen rond huurdersdata en AI gebruik",
          "Onderbouwing bij vragen van toezicht, accountant of huurdersvertegenwoordiging",
          "Scherper zicht op leveranciersafhankelijkheid vóór contractmomenten",
          "Aantoonbare beheersing van gevoelige gegevens in de keten",
        ],
      },
      {
        h2: "Onafhankelijke positie",
        paragraphs: [
          "SovEU is niet verbonden aan sectorleveranciers of implementatiepartners. Daardoor kunnen wij leveranciersafhankelijkheid benoemen zoals die is, ook wanneer dat ongemakkelijk uitkomt.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is dit relevant voor kleinere corporaties?",
        a: "Ja. Kleinere corporaties hebben doorgaans minder eigen IT capaciteit en zijn daardoor sterker afhankelijk van leveranciers. Juist dan is bestuurlijk inzicht in die afhankelijkheid van belang.",
      },
      {
        q: "Kijkt SovEU ook naar ketenpartners?",
        a: "Ja. Uitwisseling met gemeenten, zorgpartijen en handhaving is vaak waar de meest gevoelige gegevens bewegen en waar afspraken het minst scherp zijn.",
      },
      {
        q: "Wat als wij zelf geen AI gebruiken?",
        a: "In de praktijk zit AI functionaliteit vaak al in bestaande software van leveranciers. Ook dan is overzicht nodig, omdat de corporatie verantwoordelijk blijft voor de verwerking.",
      },
      {
        q: "Sluit dit aan op onze bestaande risicoparagraaf?",
        a: "Ja. Het risicobeeld is opgezet in bestuurlijke taal en kan worden gebruikt in interne risicorapportages en verantwoording richting de raad van commissarissen.",
      },
    ],
    praktijkvoorbeeld: {
      title: "Een woningcorporatie die leveranciersafhankelijkheid wilde wegen",
      situatie: [
        "Een corporatie met ruim 15.000 verhuureenheden was voor haar primaire processen sterk afhankelijk van een beperkt aantal sectorleveranciers. Huurdersdossiers en betalingsgegevens stonden verspreid over meerdere systemen en in klantcontact en debiteurenbeheer werd AI functionaliteit gebruikt. De raad van commissarissen stelde vragen over de beheersing van huurdersdata.",
        "SovEU bracht de datastromen, AI toepassingen en leveranciersafhankelijkheid samen in een bestuurlijk risicobeeld, inclusief concentratierisico en de subverwerkers achter de hoofdleveranciers.",
      ],
      resultaat: [
        "Een risicobeeld dat bruikbaar was voor directeur bestuurder en raad van commissarissen",
        "Scherper zicht op leveranciersafhankelijkheid vóór een contractmoment",
        "Prioritering van maatregelen rond huurdersdata",
      ],
    },
    relatedServices: [
      "bestuursscan-ai-en-data",
      "data-datalek-risicoscan",
      "regiebeeld-digitale-afhankelijkheden",
      "ai-beleid-opstellen",
    ],
    relatedArticles: [
      "datalek-gemeente-onderwijs-avg-data-risicos",
      "digitale-afhankelijkheid-overheid-cloud-act",
      "ai-act-readiness-2026-mkb-adviesbureaus",
    ],
    ctaTitle: "Digitale regie voor uw corporatie",
    ctaText:
      "Wij bespreken graag welke datarisico's, AI toepassingen en leveranciersafhankelijkheden binnen uw corporatie bestuurlijke aandacht vragen.",
    ctaLabel: "Plan een vrijblijvend gesprek",
  },

  {
    slug: "zorg-en-semi-publiek",
    to: "/sectoren/zorg-en-semi-publiek",
    h1: "Data, AI en digitale regie voor zorg en semipublieke organisaties",
    navLabel: "Zorg en semipubliek",
    anchorText: "Data, AI en digitale regie voor zorg en semipublieke organisaties",
    seoTitle:
      "Data, AI en digitale regie voor zorg en semipublieke organisaties | SovEU",
    seoDescription:
      "SovEU helpt zorg en semipublieke organisaties met AI governance, datagovernance, AVG, datalekrisico en digitale afhankelijkheid.",
    summary:
      "AI governance, datagovernance en digitale regie voor zorgorganisaties, GGD'en, veiligheidsregio's en maatschappelijke organisaties.",
    intro: [
      "Zorgorganisaties, GGD'en, veiligheidsregio's en maatschappelijke organisaties verwerken bijzondere persoonsgegevens in processen waarin snelheid en zorgvuldigheid tegelijk nodig zijn. Cliëntdossiers, gezondheidsgegevens, crisisinformatie en meldingen vragen een hoger beschermingsniveau dan gewone bedrijfsdata, terwijl de dagelijkse praktijk juist snelle uitwisseling vergt.",
      "SovEU helpt deze organisaties om AI gebruik, datastromen, datalekrisico en digitale afhankelijkheid bestuurlijk in beeld te brengen. Niet om de praktijk te vertragen, maar om te zorgen dat de organisatie kan uitleggen welke keuzes zij maakt en waarom.",
    ],
    sections: [
      {
        h2: "Waar zorg en semipublieke organisaties tegenaan lopen",
        bullets: [
          "Bijzondere persoonsgegevens in dossiers, meldingen en crisisprocessen",
          "Uitwisseling met ketenpartners waarbij grondslagen niet altijd scherp zijn",
          "AI ondersteuning in triage, planning, rapportage en administratie",
          "Spraak naar tekst en samenvattingsfuncties waarin gevoelige informatie belandt",
          "Cloudplatforms voor kritieke processen met beperkte uitwijkmogelijkheid",
          "Ongestructureerde opslag met dossierfragmenten, exports en verslagen",
          "Toezicht en verantwoording vanuit inspecties, raad van toezicht en bestuur",
        ],
      },
      {
        h2: "AI in zorg en crisisprocessen",
        paragraphs: [
          "AI toepassingen komen in deze sector zelden binnen als groot project. Ze komen mee met bestaande software: een samenvattingsfunctie in het dossiersysteem, spraakherkenning bij rapportage, of een planningsmodule die keuzes voorstelt. Elk van die functies verwerkt gevoelige gegevens en kan invloed hebben op de zorg of hulp die iemand krijgt.",
          "Wij brengen in kaart waar dit gebeurt, welke gegevens ermee worden verwerkt, hoe menselijke tussenkomst is geregeld en welke documentatie nodig is om verantwoord gebruik te kunnen aantonen.",
        ],
      },
      {
        h2: "Wat SovEU in kaart brengt",
        bullets: [
          "AI toepassingen en AI functies binnen kernsystemen en ondersteunende software",
          "Datastromen rond cliëntgegevens, dossiers, meldingen en crisisinformatie",
          "Uitwisseling met ketenpartners, gemeenten en zorgverleners",
          "AVG praktijk: grondslagen bij bijzondere gegevens, DPIA's, bewaartermijnen",
          "Datalekrisico en incidentgereedheid, inclusief de 72 uur praktijk",
          "Digitale afhankelijkheid, jurisdictie en continuïteit bij kritieke platforms",
          "Governance: eigenaarschap, besluitvorming en toezicht",
        ],
      },
      {
        h2: "Wat het oplevert",
        bullets: [
          "Een bestuurlijk risicobeeld voor bestuur, directie en raad van toezicht",
          "Prioritering van maatregelen rond bijzondere persoonsgegevens",
          "Een werkbaar kader voor AI gebruik dat de praktijk niet blokkeert",
          "Onderbouwing richting inspectie, toezicht en ketenpartners",
          "Aantoonbare beheersing van AI gebruik en datarisico's",
        ],
      },
      {
        h2: "Onafhankelijke positie",
        paragraphs: [
          "SovEU levert geen systemen en geen implementatiecapaciteit. Onze rol is die van onafhankelijke regiepartij: analyseren, duiden en bestuurlijk uitlegbaar maken, zonder belang bij een specifieke oplossing.",
        ],
      },
    ],
    faqs: [
      {
        q: "Voor welke organisaties is dit bedoeld?",
        a: "Voor zorgorganisaties, GGD'en, veiligheidsregio's, welzijns en jeugdorganisaties en andere semipublieke instellingen met maatschappelijke verantwoordelijkheid en gevoelige gegevens.",
      },
      {
        q: "Kijkt SovEU naar medische inhoud?",
        a: "Nee. Wij beoordelen governance, datastromen, risico's en verantwoording. De medische en zorginhoudelijke beoordeling blijft bij de professionals binnen de organisatie.",
      },
      {
        q: "Kan dit samen met onze FG en informatiebeveiliging?",
        a: "Ja, en dat is ook de bedoeling. Wij versterken bestaande rollen met een onafhankelijk bestuurlijk beeld en een gezamenlijke prioritering.",
      },
      {
        q: "Wat als AI functies al zijn ingeschakeld door een leverancier?",
        a: "Dan is het extra belangrijk om dat in beeld te brengen. De organisatie blijft verantwoordelijk voor de verwerking, ook wanneer de functionaliteit door de leverancier is geactiveerd.",
      },
    ],
    praktijkvoorbeeld: {
      title: "Een zorgorganisatie met AI functies in bestaande software",
      situatie: [
        "Bij een zorgorganisatie waren AI functies ongemerkt meegekomen met bestaande software: een samenvattingsfunctie in het dossiersysteem en spraakherkenning bij rapportage. Beide verwerkten bijzondere persoonsgegevens. Uitwisseling met ketenpartners verliep soms zonder scherpe grondslagen en de organisatie twijfelde of zij een datalek binnen 72 uur zou kunnen onderbouwen.",
        "SovEU bracht in kaart waar AI gebruik en datastromen liepen, hoe menselijke tussenkomst was geregeld en hoe de incidentgereedheid ervoor stond, vertaald naar een bestuurlijk beeld voor bestuur en raad van toezicht.",
      ],
      resultaat: [
        "Inzicht in AI functies binnen kernsystemen en ondersteunende software",
        "Een werkbaar kader voor AI gebruik dat de zorgpraktijk niet blokkeerde",
        "Aandachtspunten rond de 72 uur praktijk geprioriteerd",
      ],
    },
    relatedServices: [
      "bestuursscan-ai-en-data",
      "data-datalek-risicoscan",
      "ai-beleid-opstellen",
      "regiebeeld-digitale-afhankelijkheden",
    ],
    relatedArticles: [
      "datalek-gemeente-onderwijs-avg-data-risicos",
      "ai-act-readiness-2026-mkb-adviesbureaus",
      "digitale-afhankelijkheid-overheid-cloud-act",
    ],
    ctaTitle: "Grip op AI en data in uw organisatie",
    ctaText:
      "Wij verkennen graag welke AI toepassingen en datarisico's binnen uw organisatie bestuurlijke aandacht vragen.",
    ctaLabel: "Neem contact op",
  },
];

export function sectorBySlug(slug) {
  return SECTORS.find((s) => s.slug === slug);
}
